import { Link } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { useCompetencyPeople } from "@/hooks/use-competencies";
import { originLinkSearch } from "@/lib/detail-origin";

/**
 * Everyone who has this competency, derived transitively: a person holds a role
 * and that role requires this competency. Each row links to the person page
 * (carrying the competency as origin so the trail returns here) and shows the
 * connecting role(s) so the derivation is visible. Mirrors SkillPeopleSection.
 */
export function CompetencyPeopleSection({
  competencyId,
  competencyName,
}: {
  competencyId: string;
  competencyName: string;
}) {
  const { data = [], isLoading, isError } = useCompetencyPeople(competencyId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return <p className="text-sm text-muted-foreground">Unable to load people.</p>;
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No one has this competency yet. People gain a competency by holding a role that requires it.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {data.map((person) => (
        <li key={person.id}>
          <Link
            to="/dashboard/persons/$personId"
            params={{ personId: person.id }}
            search={originLinkSearch("competency", competencyId, competencyName)}
            className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-foreground">{person.name}</span>
              {person.title && (
                <span className="truncate text-xs text-muted-foreground">{person.title}</span>
              )}
            </span>
            <span className="shrink-0 text-right text-xs text-muted-foreground">
              via {person.roles.join(", ")}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
